import { useCallback, useEffect, useRef, useState } from 'react';

export type ConfettiState = {
	isActive: boolean;
	intensity: number;
	duration: number;
	triggerId: number;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const createBaseState = (duration: number): ConfettiState => ({
	isActive: false,
	intensity: 0.4,
	duration,
	triggerId: 0,
});

/**
 * Custom hook to manage confetti overlay state with configurable intensity and duration.
 * Returns [confettiState, triggerConfetti].
 */
export function useConfetti(defaultDuration: number = 4000): [ConfettiState, (options?: { intensity?: number; duration?: number }) => void] {
	const [state, setState] = useState<ConfettiState>(() => createBaseState(defaultDuration));
	const timeoutRef = useRef<number | null>(null);

	const clearTimer = useCallback(() => {
		if (timeoutRef.current !== null) {
			window.clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	useEffect(() => () => clearTimer(), [clearTimer]);

	const triggerConfetti = useCallback(
		(options?: { intensity?: number; duration?: number }) => {
			const intensity = clamp(options?.intensity ?? 0.4, 0, 1);
			const duration = options?.duration ?? defaultDuration;
			const triggerId = Date.now();

			clearTimer();
			setState({ isActive: true, intensity, duration, triggerId });
			timeoutRef.current = window.setTimeout(() => {
				setState((prev) => ({ ...prev, isActive: false }));
				timeoutRef.current = null;
			}, duration);
		},
		[clearTimer, defaultDuration],
	);

	return [state, triggerConfetti];
}
